import i18n from "@/i18n.ts";
import { ApiApprovalCategoryType } from "@/types/approvals/approval-policy.types.ts";
import {
  ApiAccessControlList,
  ApiApprovalRequest,
  ApiApprovalRequestStatus,
} from "@/types/approvals/approval.types.ts";
import {
  ApiCalendarHolidayDate,
  ApiCalendarHolidayDateType,
} from "@/types/calendar-holiday.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import {
  ApiEmployee,
  ApiEmployeeLifecycleStatus,
  ApiEmployeeShort,
} from "@/types/employees/employee.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";
import {
  ApiTimeOffAccrualFrequencyTypes,
  ApiTimeOffCategory,
  ApiTimeOffCategoryShort,
  ApiTimeOffCategoryType,
  ApiTimeOffPaidType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import { SelectItem } from "@skailer-com/uikit";

export enum ApiTimeOffLogType {
  initial_balance = "initial_balance",
  balance_import = "balance_import",
  accrual = "accrual",
  carryover = "carryover",
  expire = "expire",
  used = "used",
  adjustment = "adjustment",
  canceled = "canceled",
  expiration = "expiration",
}

export enum ApiTimeOffLogBalanceType {
  earnings = "earnings",
  spendings = "spendings",
}

export interface ApiTimeOffBreakdownItem {
  date: Date;
  amount: number;
  min_amount: number;
  max_amount: number;
  unit_type: ApiTimeOffPeriodTrackingTime;
  type_date: ApiCalendarHolidayDateType;
}

export interface ApiTimeOffBreakdown {
  items: ApiTimeOffBreakdownItem[];
  total_amount: number;
  is_manual_modified: boolean;
}

export interface ApiTimeOffLog {
  id: number;
  date_adjustment: Date;
  employee: ApiEmployee;
  category: ApiTimeOffCategoryShort;
  balance: string;
  balance_after: string | null;
  is_accrual_capped: boolean;
  action: ApiTimeOffLogType;
  balance_type: ApiTimeOffLogBalanceType;
  date_expire: Date | null;
  created_by?: ApiEmployeeShort;
  date_created: Date;
  date_from: Date | undefined;
  date_to: Date | undefined;
  comment: string | null;
  files: ApiFile[];
  breakdown?: ApiTimeOffBreakdown;
}

export interface ApiTimeOffAdjustment {
  employee_id: number;
  category_id: number;
  balance: number;
  date_expire: Date | null;
  date_adjustment: Date;
  comment: string | null;
}

export type ApiTimeOffAdjustmentPayload = Omit<ApiTimeOffAdjustment, "id">;

export const mockTimeOffAdjustment: ApiTimeOffAdjustmentPayload = {
  employee_id: 0,
  category_id: 0,
  balance: 0,
  date_expire: null,
  date_adjustment: new Date(),
  comment: "",
};

export const getMockTimeOffAdjustment = (employee: ApiEmployeeShort) => {
  return {
    ...mockTimeOffAdjustment,
    employee_id: employee.id,
  } as ApiTimeOffAdjustmentPayload;
};

export enum ApiTimeOffAdjustmentType {
  increase = "increase",
  decrease = "decrease",
}

export interface ApiTimeOffCalendarFilter extends ApiFilterBase {
  employee_id?: number;
  manager_ids?: number[];
  date_from: Date | null;
  date_to: Date | null;
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  team_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  category_ids?: number[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
  text?: string;
}

export interface ApiTimeOffCalendarHoliday {
  date: Date;
  title: string;
  id: number;
  type: ApiTimeOffPaidType;
}

export interface ApiTimeOffData {
  date_from: Date;
  date_to: Date;
  category: ApiTimeOffCategoryShort;
  balance_requested: string | null;
}

export interface ApiTimeOffLogFullPreview {
  id: number;
  status: ApiApprovalRequestStatus;
  time_off_data: ApiTimeOffData;
  acl: ApiAccessControlList;
}

export interface ApiEmployeeTimeOffCalendar {
  employee: ApiEmployee;
  requests: ApiTimeOffLogFullPreview[];
  calendar_holidays: ApiCalendarHolidayDate[];
}

export enum TimeOffCalendarPeriodType {
  oneMonth = "oneMonth",
  threeMonth = "threeMonth",
  oneYear = "year",
}

export interface TimeOffCalendarPeriodModel {
  type: TimeOffCalendarPeriodType;
  view: string;
  title: string;
}

export enum ApiTimeOffHistoryLogsType {
  spendings = "spendings",
  earnings = "earnings",
}

export interface ApiEmployeeTimeOffHistoryFilter {
  year: number;
  time_off_category_id: number;
}

export interface ApiTimeOffHistory {
  filter: ApiTimeOffHistoryFilterInformation;
  chart: ApiTimeOffHistoryChartItem[];
  information?: ApiTimeOffHistoryInformation | null;
  earnings?: ApiTimeOffHistoryEarning | null;
  spendings?: ApiTimeOffHistorySpending | null;
}

export interface ApiTimeOffCalendar {
  requests: ApiTimeOffLogFullPreview[];
  calendar_holidays: ApiCalendarHolidayDate[];
}

export interface ApiTimeOffHistoryLog {
  amount: number;
  balance_type: ApiTimeOffHistoryLogsType;
  date_end?: Date | null;
  date_start: Date;
  log_type: ApiTimeOffLogType;
  approval_request_id?: number;
  adjustment_log_id?: number;
  acl: ApiAccessControlList;
}

export interface ApiTimeOffHistoryFilterInformation {
  date_start: Date;
  date_end: Date;
  time_off_category: {
    id: number;
    title: string;
    description: string;
    emoji?: string;
    color?: string;
    balance: {
      available_balance: string | null;
      forcast_balance: string | null;
      balance_requested: string | null;
    };
    period_track_type: ApiTimeOffPeriodTrackingTime;
    category_type: ApiTimeOffCategoryType;
  };
}

export interface ApiTimeOffHistoryChartItem {
  date_period: Date;
  accrual_amount?: number;
  book_amount?: number;
  spend_amount?: number;
  expired_amount?: number;
  balance: number;
}

export interface ApiTimeOffHistoryEarning {
  accrual_current_amount: number;
  accrual_for_cycle_amount: number;
  carryover_from_previous_cycle_amount: number;
}

export interface ApiTimeOffHistorySpending {
  used_amount: number;
  booked_amount: number;
}

export interface ApiTimeOffHistoryInformation {
  accrual_frequency_type: ApiTimeOffAccrualFrequencyTypes;
  accrual_frequency_data: {
    date_first: Date;
    date_second: Date;
    count: number;
  };
  accrual_amount: number;
  accrual_cap_enabled: boolean;
  accrual_cap_amount: number;
  carryover_enabled: boolean;
  carryover_amount: number | null;
  date_cycle_start: Date;
  date_cycle_end: Date;
}

export interface ApiEmployeeTimeOffCategory extends ApiTimeOffCategoryShort {
  balance: {
    available_balance: string;
    total_booked_amount: string;
  };
}

export interface ApiEmployeeTimeOffRequestsFilter extends ApiFilterBase {
  category_id?: number;
}

export interface ApiTimeOffLogFull extends ApiApprovalRequest {
  time_off_log: ApiTimeOffLog;
}

export interface ApiTimeOffLogFullDetails extends ApiTimeOffLogFull {
  time_off_category: ApiEmployeeTimeOffCategory | null;
  teammates_overlap_count: number;
}

export interface ApiTimeOffApproveRequest {
  is_bypass_approval_flow?: boolean;
  comment?: string | null;
}

export interface ApiTimeOffConfirmationCommentUpdate {
  comment: string | null;
}

export interface ApiTimeOffRequestSave {
  request_id?: number | null;
  comment?: string;
  file_uuids?: string[];
  employee_id?: number;
  time_off_data: { date_from: Date; date_to: Date; category_id: number };
  is_bypass_approval_flow?: boolean;
  breakdown?: ApiTimeOffBreakdown;
}

export interface ApiTimeOffLogFullInformation {
  date_from: Date;
  date_to: Date;
  category: ApiTimeOffCategory;
}

export interface ApiTimeOffLogFullCheck {
  possible_to_create: boolean;
  attachment_required: boolean;
  type: ApiApprovalCategoryType;
  confirming_employees: ApiEmployeeShort[];
  time_off_data: ApiTimeOffLogFullInformation;
  reject_code: ApiTimeOffLogFullRejectCode;
  balance_left: number;
  balance_requested: number;
  breakdown: ApiTimeOffBreakdown;
}

export enum ApiTimeOffLogFullRejectCode {
  attachment_required = "attachment_required",
  invalid_request = "invalid_request",
  no_approval_policy = "no_approval_policy",
  no_confirmation_employees = "no_confirmation_employees",
  time_off_category_not_found = "time_off_category_not_found",
  not_enough_days = "not_enough_days",
  not_enough_hours = "not_enough_hours",
  asked_date_from_less_then_today = "asked_date_from_less_then_today",
  asked_less_then_min_daily_amount = "asked_less_then_min_daily_amount",
  asked_less_then_min_request_total = "asked_less_then_min_request_total",
  asked_more_then_max_request_total = "asked_more_then_max_request_total",
  asked_date_from_less_then_min_notice_period = "asked_date_from_less_then_min_notice_period",
  asked_date_from_more_then_max_notice_period = "asked_date_from_more_then_max_notice_period",

  no_time_off_policy = "no_time_off_policy",
  cannot_create_overlapped_time_off_requests = "cannot_create_overlapped_time_off_requests",
  cannot_create_time_off_requests_in_probation = "cannot_create_time_off_requests_in_probation",
}

export const getCarryoverExpiringEnabled = (isHours: boolean): SelectItem[] => {
  return [
    {
      title: i18n.t("time_off:carryover_expiration_yes_title", {
        units: isHours
          ? i18n.t("time_off:hours_genitive").toLowerCase()
          : i18n.t("time_off:days_genitive").toLowerCase(),
      }),
      caption: i18n.t("time_off:carryover_expiration_yes_caption", {
        units: isHours
          ? i18n.t("time_off:hours").toLowerCase()
          : i18n.t("time_off:days").toLowerCase(),
      }),
      id: 1,
    },
    {
      title: i18n.t("time_off:carryover_expiration_no_title", {
        units: isHours
          ? i18n.t("time_off:hours").toLowerCase()
          : i18n.t("time_off:days").toLowerCase(),
      }),
      caption: i18n.t("time_off:carryover_expiration_no_caption", {
        units: isHours
          ? i18n.t("time_off:hours").toLowerCase()
          : i18n.t("time_off:days").toLowerCase(),
      }),
      id: 0,
    },
  ];
};

export const getTimeOffCalendarPeriods = (): TimeOffCalendarPeriodModel[] => {
  return [
    {
      type: TimeOffCalendarPeriodType.oneMonth,
      view: "resourceTimelineMonth",
      title: i18n.t("plurals:plural_month", { count: 1 }),
    },
    {
      type: TimeOffCalendarPeriodType.threeMonth,
      view: "resourceTimeline3Months",
      title: i18n.t("plurals:plural_month", { count: 3 }),
    },
    {
      type: TimeOffCalendarPeriodType.oneYear,
      view: "resourceTimelineYear",
      title: i18n.t("plurals:plural_year", { count: 1 }),
    },
  ];
};
